import React, { useState } from "react";
import { api, API, setToken, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ScrollText, ArrowRight, ArrowLeft, UploadCloud, FileText, Loader2, CheckCircle2,
  ShieldCheck, Sparkles, ChevronRight, X, Lock, LogIn,
} from "lucide-react";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

const EXISTING_INTERESTS = [
  "Build my ancestral timeline", "Organize my genealogy evidence", "Investigate possible tribal connections",
  "Learn about the tribes appearing in my research", "Learn a tribal language", "Explore historical tribal territories",
  "Preserve my family's records", "Continue researching missing ancestors",
];

const GOALS = [
  "Identify my American Indian / Indigenous ancestry", "Investigate a specific tribal tradition in my family",
  "Find my ancestors further back", "Identify where my family lived", "Research historical racial classifications",
  "Investigate tribal rolls or enrollment records", "Research Freedmen records", "Build a documented family tree",
  "Learn more about a tribe appearing in my genealogy", "Preserve my family's history", "Other",
];

const Field = ({ label, optional, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">{label}{optional && <span className="text-[#b9b0a4] normal-case"> — optional</span>}</span>
    <input {...props} className="mt-1 w-full rounded-lg border border-[#E4DCD3] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50" />
  </label>
);

const Area = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">{label}</span>
    <textarea {...props} className="mt-1 w-full rounded-lg border border-[#E4DCD3] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50" />
  </label>
);

const CheckList = ({ options, values, onToggle, testidPrefix }) => (
  <div className="grid sm:grid-cols-2 gap-2">
    {options.map((o) => (
      <label key={o} data-testid={`${testidPrefix}-${o.slice(0, 12).replace(/\s/g, "-").toLowerCase()}`}
        className={`flex items-start gap-2.5 rounded-lg border p-2.5 cursor-pointer text-sm transition-colors ${values.includes(o) ? "border-[#D4AF37] bg-[#FFF9E6]" : "border-[#E4DCD3] bg-white hover:border-[#D4AF37]"}`}>
        <input type="checkbox" checked={values.includes(o)} onChange={() => onToggle(o)} className="mt-0.5 accent-[#59288E]" />
        <span className="text-[#1A171D]">{o}</span>
      </label>
    ))}
  </div>
);

const Shell = ({ onHome, onLogin, children }) => (
  <div className="hp-parchment-texture min-h-screen">
    <header className="bg-[#180D24] border-b border-white/10">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <button onClick={onHome} data-testid="gateway-home-btn" className="flex items-center gap-3">
          <div className="grid place-items-center h-9 w-9 rounded-lg border border-[#D4AF37]/60"><img src="/logo.png" alt="WhatsMyTribe" className="h-7 w-7 object-contain" /></div>
          <div className="leading-tight text-left">
            <div className="font-serif text-[#FAF8F5] font-bold">WhatsMyTribe</div>
            <div className="text-[9px] uppercase tracking-[0.2em] font-mono text-[#E7C965]">Heritage Portal</div>
          </div>
        </button>
        <button onClick={onLogin} data-testid="gateway-login-btn" className="text-sm font-semibold text-[#180D24] bg-[#D4AF37] hover:bg-[#E7C965] transition-colors inline-flex items-center gap-1.5 rounded-full px-4 py-2"><LogIn size={15} /> Log In</button>
      </div>
    </header>
    <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-14">{children}</main>
    <footer className="bg-[#180D24] text-[#B4A6C8] py-10 mt-10">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <p className="text-xs uppercase tracking-[0.2em] font-mono text-[#E7C965] mb-2">Research & Membership Notice</p>
        <p className="text-[12px] leading-relaxed max-w-4xl">
          WhatsMyTribe.com provides genealogy research, historical research and educational resources. A genealogy report,
          family tradition, surname, geographic association, DNA result, historical racial classification or other research
          finding does not by itself establish citizenship, enrollment, membership or legal recognition by any tribal nation.
          Tribal nations establish their own citizenship and enrollment requirements. The $20 charge is a one-time genealogy
          research fee. The $5 Heritage Portal membership is a recurring monthly subscription that renews until canceled.
        </p>
        <div className="mt-6 text-sm text-[#8f83a3]">Powered by <span className="text-[#E7C965] font-medium">ISelfLawAmMaster.com</span></div>
      </div>
    </footer>
  </div>
);

export default function Gateway({ initialScreen = "choose", onHome, onAuthed }) {
  const [screen, setScreen] = useState(initialScreen);
  const goHome = () => setScreen("choose");

  return (
    <Shell onHome={goHome} onLogin={() => setScreen("login")}>
      {screen === "choose" && <Choose setScreen={setScreen} />}
      {screen === "login" && <LoginForm onAuthed={onAuthed} setScreen={setScreen} />}
      {screen === "existing" && <ApplicationFlow mode="existing" />}
      {screen === "new" && <ApplicationFlow mode="new" />}
    </Shell>
  );
}

/* ---------- Choose ---------- */
function Choose({ setScreen }) {
  return (
    <div data-testid="gateway-choose">
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-[0.25em] font-mono text-[#8F7220] mb-3">Start Your Heritage Journey</div>
        <h1 className="font-serif font-bold text-4xl sm:text-5xl text-[#180D24]">What's Your Tribe?</h1>
        <p className="mt-4 text-lg text-[#4a4550]">
          Your family history is waiting to be researched. Whether you already completed your Tribal Genealogy Report
          through ISelfLawAmMaster.com or you're beginning from scratch, WhatsMyTribe.com gives you a place to organize
          your research, investigate your ancestry, explore tribal cultures, and preserve your family's story.
        </p>
        <div className="mt-6 text-xs uppercase tracking-[0.22em] font-mono text-[#59288E]">How would you like to begin?</div>
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        {/* Option 1 */}
        <div className="rounded-3xl bg-white border border-[#E4DCD3] p-7 flex flex-col hover:border-[#D4AF37] transition-colors" data-testid="option-existing-card">
          <div className="text-xs font-mono uppercase tracking-wider text-[#8F7220]">Option 1</div>
          <h2 className="mt-1 font-serif text-2xl font-bold text-[#180D24]">I already have my Tribal Genealogy Report</h2>
          <p className="mt-3 text-sm text-[#4a4550] flex-1">
            Completed your genealogy research through ISelfLawAmMaster.com? Upload your completed report and we'll use it
            to begin building your Heritage Portal.
          </p>
          <div className="mt-4 rounded-xl bg-[#F3EEFA] p-4">
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-2xl font-bold text-[#59288E]">First 30 Days Free</span>
            </div>
            <div className="text-sm text-[#59288E] mt-1">then <span className="font-semibold">$5/month</span> Heritage Portal Membership</div>
          </div>
          <button data-testid="choose-existing-btn" onClick={() => setScreen("existing")}
            className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#2A1442] text-white px-6 py-3 font-semibold hover:bg-[#482276] transition-colors">
            Upload My Report <ArrowRight size={17} />
          </button>
        </div>

        {/* Option 2 */}
        <div className="rounded-3xl hp-purple-panel text-white p-7 flex flex-col border border-[#D4AF37]/30" data-testid="option-new-card">
          <div className="text-xs font-mono uppercase tracking-wider text-[#E7C965]">Option 2</div>
          <h2 className="mt-1 font-serif text-2xl font-bold text-[#FAF8F5]">I need a genealogy report</h2>
          <p className="mt-3 text-sm text-[#CDBFE0] flex-1">
            Don't have a report yet? Start here. Complete your genealogy application and tell us everything you know
            about your family.
            <span className="block mt-3 rounded-lg bg-white/5 border border-[#D4AF37]/30 p-3 text-[#E9E2F2]">
              <span className="font-semibold text-[#E7C965]">NOTE:</span> To get a Full Genealogy Analysis with a physical
              Tribal ID, Foreign National Passport &amp; Paperwork visit our Parent Network{" "}
              <a href="https://iselflawammaster.com/webinar-registration/" target="_blank" rel="noopener noreferrer"
                className="underline underline-offset-2 text-[#E7C965] hover:text-white">ISelfLawAmMaster.com</a>.
            </span>
          </p>
          <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#E9E2F2]">One-Time Genealogy Research Fee</span>
              <span className="font-serif text-xl font-bold text-[#E7C965]">$20</span>
            </div>
            <div className="h-px bg-white/10" />
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#E9E2F2]">Heritage Portal Membership</span>
              <span className="font-serif text-xl font-bold text-[#E7C965]">$5<span className="text-sm font-normal">/mo</span></span>
            </div>
          </div>
          <button data-testid="choose-new-btn" onClick={() => setScreen("new")}
            className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-[#180D24] px-6 py-3 font-bold hover:bg-[#E7C965] transition-colors">
            Start My Application <ArrowRight size={17} />
          </button>
        </div>
      </div>

      <div className="mt-8 text-center text-sm text-[#4a4550]">
        Already a student?{" "}
        <button data-testid="choose-login-btn" onClick={() => setScreen("login")} className="text-[#59288E] font-semibold underline underline-offset-4 hover:text-[#180D24]">Log In</button>
      </div>
    </div>
  );
}

/* ---------- Login ---------- */
function LoginForm({ onAuthed, setScreen }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      toast.success(`Welcome back, ${data.user.name}`);
      onAuthed(data.user);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="gateway-login">
      <div className="text-center mb-7">
        <div className="text-xs uppercase tracking-[0.25em] font-mono text-[#8F7220] mb-2">Welcome Back</div>
        <h1 className="font-serif font-bold text-3xl text-[#180D24]">Log in to your Heritage Portal</h1>
      </div>
      <div className="rounded-2xl bg-white border border-[#E4DCD3] p-7 space-y-4">
        <Field label="Email Address" type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
        <Field label="Password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="••••••••" />
        <button data-testid="login-submit" onClick={submit} disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2A1442] text-white py-3 font-semibold hover:bg-[#482276] transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Log In
        </button>
        <div className="text-center text-sm text-[#756D7E]">
          New here? <button onClick={() => setScreen("choose")} className="text-[#59288E] font-semibold underline underline-offset-4">Start your journey</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Existing student flow now uses the shared ApplicationFlow (mode="existing") ---------- */

/* ---------- New user 8-step application ---------- */

const STEPS = ["About You", "Birth Details", "Your Parents", "Grandparents", "Great-Grandparents", "Heritage & Options", "Review & Begin"];

const GP_FIELDS = [
  ["mgm", "mgmBp", "mgmDob", "Maternal Grandmother"],
  ["mgf", "mgfBp", "mgfDob", "Maternal Grandfather"],
  ["pgm", "pgmBp", "pgmDob", "Paternal Grandmother"],
  ["pgf", "pgfBp", "pgfDob", "Paternal Grandfather"],
];
const GGP_FIELDS = [
  ["mggm", "mggmBp", "mggmDob", "Maternal GREAT Grandmother"],
  ["mggf", "mggfBp", "mggfDob", "Maternal GREAT Grandfather"],
  ["pggm", "pggmBp", "pggmDob", "Paternal GREAT Grandmother"],
  ["pggf", "pggfBp", "pggfDob", "Paternal GREAT Grandfather"],
];

const GPCombined = ({ label, nameKey, bpKey, dobKey, d, set }) => (
  <div className="rounded-xl border border-[#E4DCD3] p-4 space-y-3">
    <div className="font-serif font-semibold text-[#180D24]">{label}</div>
    <Field label="Name" optional value={d[nameKey]} onChange={set(nameKey)} placeholder="Full name" />
    <Field label="Birth Place" optional value={d[bpKey]} onChange={set(bpKey)} placeholder="City / county / state" />
    <Field label="Full Date of Birth" optional value={d[dobKey]} onChange={set(dobKey)} placeholder="MM/DD/YYYY" />
  </div>
);

function ApplicationFlow({ mode = "new" }) {
  const existing = mode === "existing";
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [d, setD] = useState({
    fullName: "", maiden: "", appellation: "", gender: "", dob: "", email: "", password: "", mailing: "",
    height: "", weight: "", eyeColor: "", hairColor: "",
    birthCityState: "", birthZip: "",
    motherName: "", motherDob: "", motherAge: "", fatherName: "", fatherDob: "", fatherAge: "",
    mgm: "", mgmBp: "", mgmDob: "", mgf: "", mgfBp: "", mgfDob: "", pgm: "", pgmBp: "", pgmDob: "", pgf: "", pgfBp: "", pgfDob: "",
    mggm: "", mggmBp: "", mggmDob: "", mggf: "", mggfBp: "", mggfDob: "", pggm: "", pggmBp: "", pggmDob: "", pggf: "", pggfBp: "", pggfDob: "",
    tribalHeritage: "", orderNumber: "", tribalKlan: "",
  });
  const set = (k) => (e) => setD((s) => ({ ...s, [k]: e.target.value }));

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const ok = picked.filter((x) => { if (x.size > 25 * 1024 * 1024) { toast.error(`${x.name} exceeds 25 MB`); return false; } return true; });
    setFiles((prev) => [...prev, ...ok].slice(0, 15));
    e.target.value = "";
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const REQUIRED = {
    0: [["fullName", "full government name"], ["gender", "gender"], ["dob", "birth date"], ["email", "email"], ["password", "password"], ["mailing", "mailing address"]],
    1: [["birthCityState", "city & state where you were born"], ["birthZip", "local ZIP code"]],
    2: [["motherName", "mother's name"], ["fatherName", "father's name"]],
  };
  if (existing) REQUIRED[5] = [["orderNumber", "original order number"], ["tribalKlan", "tribal klan listed on your report"]];
  const validateStep = (s) => {
    const missing = (REQUIRED[s] || []).filter(([k]) => !(d[k] || "").trim());
    return missing.length ? `Please complete: ${missing.map(([, l]) => l).join(", ")}` : null;
  };

  const next = () => {
    const err = validateStep(step);
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1)); window.scrollTo(0, 0);
  };
  const back = () => { setStep((s) => Math.max(s - 1, 0)); window.scrollTo(0, 0); };

  const submit = async () => {
    for (let s = 0; s <= 6; s++) {
      const err = validateStep(s);
      if (err) { toast.error(err); setStep(s); window.scrollTo(0, 0); return; }
    }
    setBusy(true);
    try {
      let data;
      if (existing) {
        const fd = new FormData();
        fd.append("application", JSON.stringify(d));
        fd.append("order_number", d.orderNumber);
        fd.append("tribal_klan", d.tribalKlan);
        fd.append("email", d.email);
        fd.append("password", d.password);
        fd.append("name", d.fullName);
        fd.append("phone", "");
        fd.append("origin_url", ORIGIN);
        files.forEach((f) => fd.append("files", f));
        ({ data } = await api.post("/onboarding/existing", fd));
      } else {
        ({ data } = await api.post("/onboarding/new", {
          email: d.email, password: d.password, name: d.fullName, phone: "",
          origin_url: ORIGIN, application: d,
        }));
      }
      setToken(data.token);
      toast.success("Application saved — redirecting to secure checkout…");
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };


  return (
    <div className="max-w-3xl mx-auto" data-testid={existing ? "existing-flow" : "new-flow"}>
      <div className="text-xs font-mono uppercase tracking-wider text-[#8F7220]">{existing ? "Option 1 — Already Have Genealogy Done" : "Tribal Genealogy Application"}</div>
      <h1 className="font-serif font-bold text-3xl text-[#180D24] mt-1">Step {step + 1} — {STEPS[step]}</h1>

      <div className="mt-5 flex items-center gap-1.5">
        {STEPS.map((s, i) => (<div key={s} className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: i <= step ? "#D4AF37" : "#ECE5D8" }} />))}
      </div>

      <div className="mt-7 rounded-2xl bg-white border border-[#E4DCD3] p-6">
        {step === 0 && (
          <div className="space-y-4" data-testid="new-step-1">
            <Field label="Full Complete Government Name" data-testid="new-fullname" value={d.fullName} onChange={set("fullName")} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Maiden Name" optional value={d.maiden} onChange={set("maiden")} />
              <Field label="Appellation to show on your documents" optional value={d.appellation} onChange={set("appellation")} placeholder="e.g. Bey, El, Dey" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Gender" value={d.gender} onChange={set("gender")} />
              <Field label="Birth Date" value={d.dob} onChange={set("dob")} placeholder="MM/DD/YYYY" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Email Address" type="email" data-testid="new-email" value={d.email} onChange={set("email")} />
              <Field label="Password" type="password" data-testid="new-password" value={d.password} onChange={set("password")} />
            </div>
            <Field label="Mailing Address" value={d.mailing} onChange={set("mailing")} placeholder="Street, City, State, ZIP" />
          </div>
        )}
        {step === 1 && (
          <div className="space-y-4">
            <Field label="CITY & STATE where you were born" value={d.birthCityState} onChange={set("birthCityState")} placeholder="e.g. Tulsa, Oklahoma" />
            <Field label="Local ZIP Code where you were born" value={d.birthZip} onChange={set("birthZip")} placeholder="e.g. 74103" />
          </div>
        )}
        {step === 2 && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-[#E4DCD3] p-4 space-y-3">
              <div className="font-serif font-semibold text-[#180D24]">Mother</div>
              <Field label="Mother's Name" value={d.motherName} onChange={set("motherName")} />
              <Field label="Mother's Birth Date" optional value={d.motherDob} onChange={set("motherDob")} />
              <Field label="Mother's Age at Your Birth" optional value={d.motherAge} onChange={set("motherAge")} />
            </div>
            <div className="rounded-xl border border-[#E4DCD3] p-4 space-y-3">
              <div className="font-serif font-semibold text-[#180D24]">Father</div>
              <Field label="Father's Name" value={d.fatherName} onChange={set("fatherName")} />
              <Field label="Father's Birth Date" optional value={d.fatherDob} onChange={set("fatherDob")} />
              <Field label="Father's Age at Your Birth" optional value={d.fatherAge} onChange={set("fatherAge")} />
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="grid sm:grid-cols-2 gap-4">
            {GP_FIELDS.map(([nk, bk, dk, label]) => <GPCombined key={nk} label={label} nameKey={nk} bpKey={bk} dobKey={dk} d={d} set={set} />)}
          </div>
        )}
        {step === 4 && (
          <div className="grid sm:grid-cols-2 gap-4">
            {GGP_FIELDS.map(([nk, bk, dk, label]) => <GPCombined key={nk} label={label} nameKey={nk} bpKey={bk} dobKey={dk} d={d} set={set} />)}
          </div>
        )}
        {step === 5 && (
          <div className="space-y-4" data-testid="new-step-7">
            <Area label="Any Tribal heritage that YOU are aware of" rows={3} value={d.tribalHeritage} onChange={set("tribalHeritage")} placeholder="Describe any tribe, nation or community connected to your family, and which relative it came from." />
            <p className="text-[11px] text-[#B45309] bg-[#FFFBEB] rounded-lg p-3">Stated heritage is treated as a research lead — not automatic proof of tribal ancestry.</p>
            {existing ? (
              <>
                <Field label="Original Order Number" data-testid="existing-order" value={d.orderNumber} onChange={set("orderNumber")} placeholder="e.g. ISM-100245" />
                <Field label="What Tribal Klan is listed on your Genealogy Report?" data-testid="existing-klan" value={d.tribalKlan} onChange={set("tribalKlan")} placeholder="e.g. the clan / klan named on your report" />
                <div>
                  <div className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mb-2">Upload your Genealogy Report &amp; documents <span className="normal-case text-[#b9b0a4]">— optional (PDF, JPG, PNG • add multiple)</span></div>
                  <input id="existing-files" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*" hidden data-testid="existing-file-input" onChange={onFiles} />
                  <button onClick={() => document.getElementById("existing-files").click()} data-testid="existing-choose-file"
                    className="w-full flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#D4AF37]/50 bg-[#FFF9E6] py-6 text-[#8F7220] hover:border-[#D4AF37] transition-colors">
                    <UploadCloud size={22} /> <span className="font-semibold">Choose files</span>
                    <span className="text-xs">Add one or more documents • Max 25 MB each</span>
                  </button>
                  {files.length > 0 && (
                    <ul className="mt-3 space-y-2" data-testid="existing-file-list">
                      {files.map((f, i) => (
                        <li key={i} className="flex items-center gap-3 rounded-xl border border-[#E4DCD3] bg-[#FAF8F5] px-4 py-2.5">
                          <FileText size={16} className="text-[#59288E]" /><span className="flex-1 text-sm text-[#180D24] truncate">{f.name}</span>
                          <button onClick={() => removeFile(i)} className="text-[#9CA3AF] hover:text-[#B91C1C]"><X size={15} /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            ) : (
              <Field label="ISelfLawAmMaster.com Order # (if you have one)" optional value={d.orderNumber} onChange={set("orderNumber")} />
            )}
            <div className="rounded-xl bg-[#FFF9E6] border border-[#D4AF37]/40 p-4 text-sm text-[#8F7220] flex items-center gap-2">
              <Sparkles size={16} /> After checkout you can upload more photos and documents into your Ancestral Vault (PDF, JPG, PNG).
            </div>
          </div>
        )}
        {step === 6 && (
          <div data-testid={existing ? "existing-review" : "new-step-8"}>
            <h3 className="font-serif text-xl font-semibold text-[#180D24] mb-4">Your Order</h3>
            <div className="rounded-xl border border-[#E4DCD3] divide-y divide-[#E4DCD3]">
              {!existing && (
                <div className="flex items-center justify-between p-4">
                  <div><div className="font-medium text-[#180D24]">Tribal Genealogy Research Report</div><div className="text-xs text-[#756D7E]">One-Time Research Fee</div></div>
                  <div className="font-serif text-xl font-bold text-[#180D24]">$20.00</div>
                </div>
              )}
              <div className="flex items-center justify-between p-4">
                <div><div className="font-medium text-[#180D24]">WhatsMyTribe.com Heritage Portal</div><div className="text-xs text-[#756D7E]">{existing ? "First 30 days free, then ongoing membership" : "Ongoing Membership"}</div></div>
                <div className="font-serif text-xl font-bold text-[#180D24]">$5.00<span className="text-sm font-normal">/mo</span></div>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#F3EEFA]">
                <div className="font-serif text-lg font-bold text-[#180D24]">Due Today</div>
                <div className="font-serif text-2xl font-bold text-[#59288E]">{existing ? "$0.00" : "$20.00"}</div>
              </div>
            </div>
            <p className="mt-3 text-xs text-[#756D7E]">{existing ? "Your first 30 days are free, then $5.00/month. Your membership automatically renews until canceled." : "Then $5.00/month. Your monthly membership begins after 30 days and automatically renews until canceled."}</p>
            <button data-testid={existing ? "existing-submit" : "new-submit"} onClick={submit} disabled={busy}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-[#180D24] py-4 text-base font-bold hover:bg-[#E7C965] transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />} {existing ? "Continue to Secure Checkout — Start Free" : "Continue to Secure Checkout — $20 Today"}
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button onClick={back} disabled={step === 0} className="inline-flex items-center gap-1.5 text-sm text-[#59288E] font-semibold disabled:opacity-40"><ArrowLeft size={16} /> Back</button>
        {step < STEPS.length - 1 && (
          <button data-testid="new-next" onClick={next} className="inline-flex items-center gap-1.5 rounded-full bg-[#2A1442] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#482276] transition-colors">
            Continue <ChevronRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
